import type { PublicEchoFeedbackRecord } from "@/server/echoFeedback";
import type {
  EchoflicksSessionFeedback,
  EchoflicksSessionFramingEvent,
} from "../types";
import { correlateFeedbackToFraming } from "../echoflicks/framingDiagnostics";
import {
  framingSnapshotFromMetadataSection,
  type FeedbackFramingCorrelationStatus,
  type FeedbackFramingSnapshot,
} from "./feedbackFramingSnapshot";

export type FeedbackFramingCorrelation = {
  sceneId: string | null;
  attemptId: string | null;
  taskId: string | null;
  framingPlanId: string | null;
  planDigest: string | null;
  videoFrame: number | null;
  timelineMatch: "exact_interval" | "unavailable";
  /**
   * exact: frozen snapshot from the authorized inspector (CROP-WB-003).
   * derived: unique current-timeline match. ambiguous/unmatched/stale are
   * honest non-exact states. Absent on legacy reports.
   */
  status: FeedbackFramingCorrelationStatus | "unavailable";
  frozenSnapshot: FeedbackFramingSnapshot | null;
};

const unavailableCorrelation: FeedbackFramingCorrelation = {
  sceneId: null,
  attemptId: null,
  taskId: null,
  framingPlanId: null,
  planDigest: null,
  videoFrame: null,
  timelineMatch: "unavailable",
  status: "unavailable",
  frozenSnapshot: null,
};

export function effectiveFeedbackCorrelation(
  report: PublicEchoFeedbackRecord,
  events: EchoflicksSessionFramingEvent[] = [],
): FeedbackFramingCorrelation {
  // Legacy public reports never claimed a scene or geometry space. Do not
  // manufacture one by correlating their timestamp against today's timeline.
  if (report.legacy || report.metadata.status === "unavailable_legacy") {
    return unavailableCorrelation;
  }

  // Prefer the frozen correlation captured at submission time: it keeps
  // pointing at the evidence the operator saw even after later retries.
  const frozen = framingSnapshotFromMetadataSection(
    (report.metadata as { framing?: unknown }).framing,
  );
  if (frozen && frozen.correlationStatus === "exact" && frozen.sceneId) {
    return {
      sceneId: frozen.sceneId,
      attemptId: frozen.attemptId,
      taskId: frozen.taskId,
      framingPlanId: frozen.planId,
      planDigest: frozen.planDigest,
      videoFrame: null,
      timelineMatch: "exact_interval",
      status: "exact",
      frozenSnapshot: frozen,
    };
  }

  const timeline = recordValue(report.metadata.timeline);
  const scene = recordValue(timeline?.scene);
  const metadataSceneId = stringValue(scene?.id);
  const eventCorrelation = correlateFeedbackToFraming(
    events,
    report.timestampSeconds,
  );
  if (
    timeline?.source !== "timeline_contract" ||
    timeline.confidence !== "exact" ||
    !metadataSceneId ||
    eventCorrelation.timelineMatch !== "exact_interval" ||
    eventCorrelation.sceneId !== metadataSceneId
  ) {
    return {
      ...unavailableCorrelation,
      status: frozen ? frozen.correlationStatus : "unavailable",
      frozenSnapshot: frozen,
    };
  }
  return {
    ...eventCorrelation,
    videoFrame:
      integerValue(timeline.timestampFrame) ?? eventCorrelation.videoFrame,
    status: "derived",
    frozenSnapshot: frozen,
  };
}

export function feedbackRecordToSessionFeedback(
  report: PublicEchoFeedbackRecord,
  events: EchoflicksSessionFramingEvent[] = [],
): EchoflicksSessionFeedback {
  const correlation = effectiveFeedbackCorrelation(report, events);
  const framingCategory = report.categories.some(
    (category) => category.id === "framing_crop",
  );
  return {
    id: `crm-feedback:${report.id}`,
    source_id: report.id,
    session_id: report.echo.id,
    whatsapp_user_id: "crm-private-feedback",
    feedback_text: report.comment,
    scene_id: correlation.sceneId,
    video_timestamp_ms: report.position.timestampMs,
    video_frame: correlation.videoFrame,
    feedback_category: framingCategory
      ? "framing_crop"
      : (report.categories[0]?.id ?? "other"),
    occurred_at: report.submittedAt,
    source: "crm-private-feedback",
    raw_payload: {
      feedback_report_id: report.id,
      attempt_id: correlation.attemptId,
      task_id: correlation.taskId,
      framing_plan_id: correlation.framingPlanId,
      plan_digest: correlation.planDigest,
      timeline_match: correlation.timelineMatch,
      storage_mode: report.environment.literalKey,
      metadata_status: report.metadata.status,
    },
  };
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringValue(value: unknown) {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function integerValue(value: unknown) {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : null;
}
